/**
 * The `[tool <name>]` line, and why it has to be removed from an app chat.
 *
 * Until native tool replay landed (`app-chat-tool-replay.ts`) every stored app
 * tool call was replayed to the model as exactly this text
 * (`describeAppToolPart`). A model imitates a notation it keeps reading: it
 * WRITES the line instead of calling the tool and then answers as if the result
 * had come back. The imitation is persisted as ordinary assistant text, so an
 * old transcript teaches the same trick on every later turn — which is why the
 * fix to the projection does not heal the sessions it poisoned.
 *
 * ONE module owns the notation: the backend PRODUCES it
 * ({@link formatToolNarration}, the fallback projection in
 * `model-text-projection.ts`), the backend strip and heal remove it, and
 * `PlannerMarkdown` — today the only app renderer that filters it — hides the
 * line a live turn streams before the write can strip it. Producer and matcher
 * in two packages is one rule in two hand-kept spellings, which drifts: rename a
 * tool past the name charclass below and the projection emits a line the strip,
 * the heal, the client filter and the detector all silently stop matching.
 *
 * The FAILED spelling (`[tool mutatePlan | failed]`, plan §D7) is matched too,
 * and the trade-off is deliberate. A marked line is exactly as imitable as an
 * unmarked one — it is the same notation with a suffix — so leaving it outside
 * the strip, the heal, the client filter and the narration detector would
 * re-open the imitation channel this module exists to close. The price is paid
 * one deploy long: during a rolling window an old node re-derives an unreadable
 * failed part from the dual-written `content` (`normalizeStoredChatMessage`'s
 * §6.3 fallback) and heals that line away, losing the record of the FAILURE.
 * Losing a failure cannot cause a duplicate write — the thing the record
 * protects against — while a permanent notation outside every defense can.
 */ /**
 * The marker a descriptor carries when the call FAILED, INSIDE the brackets.
 *
 * Load-bearing because a failed call carries no output by contract, so every
 * payload descriptor degrades to its opaque form — and an opaque `[chart]` reads
 * exactly like a chart that WAS drawn, leaving the model to reason about a
 * result that never existed.
 */ export const FAILED_CALL_MARKER = ' | failed';
/**
 * The one spelling of the notation: `[tool mutatePlan]`, or
 * `[tool mutatePlan | failed]` when the call failed.
 */ export function formatToolNarration(toolName, options) {
    return `[tool ${toolName}${options?.failed ? FAILED_CALL_MARKER : ''}]`;
}
/** `[chart]` → `[chart | failed]` — the marker goes inside the brackets. */ export function markDescriptorFailed(opaqueDescriptor) {
    return opaqueDescriptor.replace(/]$/, `${FAILED_CALL_MARKER}]`);
}
/**
 * A literal as a regular expression matches ONLY itself. Every metacharacter,
 * not just the `|` today's marker happens to contain: the derivation below is
 * what makes producer and matcher one rule, and escaping selectively would put
 * an expiry date on that — the next edit to {@link FAILED_CALL_MARKER} would
 * silently turn a suffix into a pattern.
 */ function escapeForRegExp(literal) {
    return literal.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}
/**
 * One occurrence of the notation, wherever it sits — derived from the same
 * marker {@link formatToolNarration} writes, so the two cannot drift apart.
 */ const TOOL_NARRATION = String.raw`\[tool [A-Za-z0-9_-]{1,64}(${escapeForRegExp(FAILED_CALL_MARKER)})?\]`;
/**
 * A WHOLE line that is nothing but the notation — the form the model imitates,
 * because it is the form the projection emitted.
 */ const TOOL_NARRATION_LINE = new RegExp(`^\\s*${TOOL_NARRATION}\\s*$`);
const TOOL_NARRATION_MENTION = new RegExp(TOOL_NARRATION);
/**
 * The text without its narration lines, or the SAME string when it has none —
 * byte-identity for an untouched answer is what keeps this safe to run over
 * every assistant message of every session.
 *
 * Only whole lines are removed. An occurrence inside a sentence ("ich rufe
 * [tool readPlan] auf") is left alone: cutting a fragment out of prose leaves a
 * mutilated sentence, and that form is not the one the model copies from the
 * transcript. {@link mentionsToolNarration} still sees it.
 */ export function stripToolNarrationLines(text) {
    const lines = text.split('\n');
    if (!lines.some(isToolNarrationLine)) return text;
    const kept = lines.filter((line)=>!isToolNarrationLine(line));
    // Removing a line between two paragraphs leaves a blank run behind, and the
    // edges of the text often go blank entirely — the strip must not be visible
    // as a hole in the answer.
    return collapseBlankRuns(kept).join('\n').trim();
}
/**
 * The parts with their narration lines removed, or the SAME array when none
 * carried any — reference identity lets a caller hand back its own message
 * object untouched, which the per-message replay caches key on.
 *
 * A text part the strip empties is DROPPED: the line was the whole part, and an
 * empty text part is neither persistable nor replayable.
 *
 * Read structurally rather than through a part type, because the two callers
 * hold different ones: streamed SDK parts are `unknown` until the persistence
 * allowlist has validated them, while a replayed transcript holds
 * `ChatMessagePart`s. The rule is the same either way.
 */ export function stripToolNarrationFromParts(parts) {
    const stripped = [];
    let changed = false;
    for (const part of parts){
        const candidate = part;
        if (candidate?.type !== 'text' || typeof candidate.text !== 'string') {
            stripped.push(part);
            continue;
        }
        const text = stripToolNarrationLines(candidate.text);
        if (text === candidate.text) {
            stripped.push(part);
            continue;
        }
        changed = true;
        if (text !== '') stripped.push({
            ...candidate,
            text
        });
    }
    return changed ? stripped : parts;
}
/** Whether the text names a tool the way a narrated turn does — anywhere in it. */ export function mentionsToolNarration(text) {
    return TOOL_NARRATION_MENTION.test(text);
}
function isToolNarrationLine(line) {
    return TOOL_NARRATION_LINE.test(line);
}
function collapseBlankRuns(lines) {
    return lines.filter((line, index)=>!isBlank(line) || !isBlank(lines[index - 1]));
}
function isBlank(line) {
    return (line ?? '').trim() === '';
}

//# sourceMappingURL=tool-narration.js.map